import { Member } from '@/lib/members/member.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { AlertTriangle, Info } from 'lucide-react';

export type MemberStatusAlertProps = {
  member: Member;
};

type AlertType = 'payment_issue' | 'inactive' | 'pending' | 'fraud' | null;

function getAlertType(member: Member): AlertType {
  if (member.memberStatus === 'Fraud') {
    return 'fraud';
  }

  if (member.memberStatus === 'Pending') {
    return 'pending';
  }

  if (member.memberStatus === 'Inactive') {
    return 'inactive';
  }

  if (member.memberStatus === 'Active' && member.paymentStatus === 'No Pago') {
    return 'payment_issue';
  }

  return null;
}

function SupportLink() {
  return (
    <a
      href="https://soporte.cinepolis.com.ar/support/home"
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
    >
      soporte
    </a>
  );
}

export default function MemberStatusAlert({ member }: MemberStatusAlertProps) {
  const alertType = getAlertType(member);

  switch (alertType) {
    case 'payment_issue':
      return (
        <Alert className="border-yellow-600/50 bg-yellow-600/10 text-yellow-700 dark:border-yellow-600 [&>svg]:text-yellow-600 [&>svg~*]:pl-9">
          <AlertTriangle className="h-6 w-6" />
          <AlertTitle>No pudimos procesar tu último pago</AlertTitle>
          <AlertDescription>
            Ponete en contacto con nuestro <SupportLink /> o agregá un medio de pago para seguir disfrutando de tus beneficios.
          </AlertDescription>
        </Alert>
      );

    case 'inactive':
      return (
        <Alert className="border-yellow-600/50 bg-yellow-600/10 text-yellow-700 dark:border-yellow-600 [&>svg]:text-yellow-600 [&>svg~*]:pl-9">
          <AlertTriangle className="h-6 w-6" />
          <AlertTitle>Tu membresía está inactiva</AlertTitle>
          <AlertDescription>
            Tu baja ya se encuentra efectiva. Si querés reingresar, por favor contactate con <SupportLink />.
          </AlertDescription>
        </Alert>
      );

    case 'pending':
      return (
        <Alert className="border-blue-500/50 bg-blue-500/10 text-blue-600 dark:border-blue-500 [&>svg]:text-blue-500 [&>svg~*]:pl-9">
          <Info className="h-6 w-6" />
          <AlertTitle>Te falta completar tu registro</AlertTitle>
          <AlertDescription>
            Necesitás validar tu número de celular para usar tus beneficios y terminar el registro. Si necesitás ayuda, contactate con <SupportLink />.
          </AlertDescription>
        </Alert>
      );

    case 'fraud':
      return (
        <Alert className="border-red-600/50 bg-red-600/10 text-red-700 dark:border-red-600 [&>svg]:text-red-600 [&>svg~*]:pl-9">
          <AlertTriangle className="h-6 w-6" />
          <AlertTitle>No pudimos habilitar tu cuenta</AlertTitle>
          <AlertDescription>
          Detectamos inconsistencias en los datos asociados a esta cuenta.          
          </AlertDescription>
        </Alert>
      );

    default:
      return null;
  }
}
